/**
 * AdminOpsApi.ts — Opérations admin/super_admin plateforme (Strangler lot 4).
 *
 * Couvre les tables opérationnelles hors domaine métier : versions APK
 * (app_version_status), kill-switches (system_feature_locks), feature
 * toggles tenant, configs ERP, historique d'import catalogue, permissions
 * utilisateur, parc devices (tenant_devices + overrides) et règles/événements
 * d'alerte assistance.
 *
 * Result<T, AppError>, zéro any, RLS = autorité (toutes ces tables sont
 * gated super_admin/admin côté policies). Sémantique des consumers legacy
 * reproduite à l'identique (mêmes filtres/tris/payloads).
 */

import { supabase } from '@/integrations/supabase/client';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase } from '@/api/errors';
import type { AppError } from '@/api/errors';
import type { Database } from '@/integrations/supabase/types';

type Tables = Database['public']['Tables'];
export type AppVersionRow = Tables['app_version_status']['Row'];
export type FeatureLockRow = Tables['system_feature_locks']['Row'];
export type TenantFeatureToggleRow = Tables['tenant_feature_toggles']['Row'];
export type TenantErpConfigRow = Tables['tenant_erp_configs']['Row'];
export type TenantImportHistoryRow = Tables['tenant_import_history']['Row'];
export type UserPermissionRow = Tables['user_permissions']['Row'];
export type TenantDeviceRow = Tables['tenant_devices']['Row'];
export type TenantDeviceOverrideRow = Tables['tenant_device_overrides']['Row'];
export type AssistanceAlertRuleRow = Tables['assistance_alert_rules']['Row'];
export type AssistanceAlertEventRow = Tables['assistance_alert_events']['Row'];
type DeviceCommandType = Database['public']['Enums']['device_command_type'];

export const AdminOpsApi = {
  // ── app_version_status (rollout APK) ─────────────────────────────────
  async listAppVersions(opts?: { status?: string }): Promise<Result<AppVersionRow[], AppError>> {
    let query = supabase.from('app_version_status').select('*');
    if (opts?.status !== undefined) query = query.eq('status', opts.status);
    const { data, error } = await query.order('version_code', { ascending: false });
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listAppVersions'));
    return ok(data ?? []);
  },

  /**
   * Liste légère des tenants pour les vues plateforme (assistance/diagnostics).
   * Rows complets (RLS super_admin), filtres internes/démo optionnels.
   */
  async listTenantsLite(opts?: {
    excludeInternal?: boolean;
    excludeDemo?: boolean;
    limit?: number;
  }): Promise<Result<Tables['tenants']['Row'][], AppError>> {
    let query = supabase.from('tenants').select('*');
    if (opts?.excludeInternal) query = query.eq('is_internal', false);
    if (opts?.excludeDemo) query = query.eq('is_demo', false);
    query = query.order('name');
    if (opts?.limit !== undefined) query = query.limit(opts.limit);
    const { data, error } = await query;
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listTenantsLite'));
    return ok(data ?? []);
  },

  async getLatestAppVersion(): Promise<
    Result<Pick<AppVersionRow, 'version_name' | 'released_at'> | null, AppError>
  > {
    const { data, error } = await supabase
      .from('app_version_status')
      .select('version_name, released_at')
      .order('released_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.getLatestAppVersion'));
    return ok(data);
  },

  async insertAppVersion(
    payload: Tables['app_version_status']['Insert'],
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase.from('app_version_status').insert(payload);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.insertAppVersion'));
    return ok(undefined);
  },

  async updateAppVersion(
    versionCode: number,
    patch: Tables['app_version_status']['Update'],
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase
      .from('app_version_status')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('version_code', versionCode);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.updateAppVersion'));
    return ok(undefined);
  },

  async deleteAppVersion(versionCode: number): Promise<Result<void, AppError>> {
    const { error } = await supabase
      .from('app_version_status')
      .delete()
      .eq('version_code', versionCode);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.deleteAppVersion'));
    return ok(undefined);
  },

  // ── system_feature_locks (kill-switches plateforme) ──────────────────
  async listFeatureLocks(opts?: {
    lockedOnly?: boolean;
  }): Promise<Result<FeatureLockRow[], AppError>> {
    let query = supabase.from('system_feature_locks').select('*');
    if (opts?.lockedOnly) query = query.eq('locked', true);
    const { data, error } = await query.order('key');
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listFeatureLocks'));
    return ok(data ?? []);
  },

  async updateFeatureLock(
    key: string,
    patch: Tables['system_feature_locks']['Update'],
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase
      .from('system_feature_locks')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('key', key);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.updateFeatureLock'));
    return ok(undefined);
  },

  async upsertFeatureLock(
    payload: Tables['system_feature_locks']['Insert'],
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase.from('system_feature_locks').upsert(payload);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.upsertFeatureLock'));
    return ok(undefined);
  },

  // ── tenant_feature_toggles ────────────────────────────────────────────
  async listTenantFeatureToggles(
    tenantId: string,
  ): Promise<Result<Array<Pick<TenantFeatureToggleRow, 'feature_key' | 'enabled'>>, AppError>> {
    const { data, error } = await supabase
      .from('tenant_feature_toggles')
      .select('feature_key, enabled')
      .eq('tenant_id', tenantId);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listTenantFeatureToggles'));
    return ok(data ?? []);
  },

  async upsertTenantFeatureToggle(
    tenantId: string,
    featureKey: string,
    enabled: boolean,
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase.from('tenant_feature_toggles').upsert(
      {
        tenant_id: tenantId,
        feature_key: featureKey,
        enabled,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'tenant_id,feature_key' },
    );
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.upsertTenantFeatureToggle'));
    return ok(undefined);
  },

  // ── tenant_erp_configs / tenant_import_history (intégrations catalogue) ──
  async listErpConfigs(tenantId: string): Promise<Result<TenantErpConfigRow[], AppError>> {
    const { data, error } = await supabase
      .from('tenant_erp_configs')
      .select('*')
      .eq('tenant_id', tenantId);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listErpConfigs'));
    return ok(data ?? []);
  },

  async upsertErpConfig(
    payload: Tables['tenant_erp_configs']['Insert'],
    onConflict?: string,
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase
      .from('tenant_erp_configs')
      .upsert(payload, onConflict ? { onConflict } : undefined);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.upsertErpConfig'));
    return ok(undefined);
  },

  async listImportHistory(
    tenantId: string,
    limit = 20,
  ): Promise<Result<TenantImportHistoryRow[], AppError>> {
    const { data, error } = await supabase
      .from('tenant_import_history')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listImportHistory'));
    return ok(data ?? []);
  },

  async insertImportHistory(
    payload: Tables['tenant_import_history']['Insert'],
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase.from('tenant_import_history').insert(payload);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.insertImportHistory'));
    return ok(undefined);
  },

  // ── user_permissions (modules par utilisateur) ────────────────────────
  async listUserPermissions(
    tenantId: string,
    userId?: string,
  ): Promise<Result<UserPermissionRow[], AppError>> {
    let query = supabase.from('user_permissions').select('*').eq('tenant_id', tenantId);
    if (userId !== undefined) query = query.eq('user_id', userId);
    const { data, error } = await query;
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listUserPermissions'));
    return ok(data ?? []);
  },

  async upsertUserPermission(
    payload: Tables['user_permissions']['Insert'],
    onConflict = 'user_id,tenant_id,module',
  ): Promise<Result<void, AppError>> {
    const { error } = await supabase.from('user_permissions').upsert(payload, { onConflict });
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.upsertUserPermission'));
    return ok(undefined);
  },

  async deleteUserPermission(filters: {
    userId: string;
    tenantId: string;
    module?: Database['public']['Enums']['permission_module'];
  }): Promise<Result<void, AppError>> {
    let query = supabase
      .from('user_permissions')
      .delete()
      .eq('user_id', filters.userId)
      .eq('tenant_id', filters.tenantId);
    if (filters.module !== undefined) query = query.eq('module', filters.module);
    const { error } = await query;
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.deleteUserPermission'));
    return ok(undefined);
  },

  // ── tenant_devices + overrides (parc Android, assistance) ────────────
  async listDevicesWithTenant(): Promise<
    Result<
      Array<
        TenantDeviceRow & {
          tenants: { name: string; slug: string | null; is_internal: boolean | null } | null;
        }
      >,
      AppError
    >
  > {
    const { data, error } = await supabase
      .from('tenant_devices')
      .select('*, tenants(name, slug, is_internal)')
      .order('last_heartbeat_at', { ascending: false, nullsFirst: false });
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listDevicesWithTenant'));
    return ok(
      (data ?? []) as Array<
        TenantDeviceRow & {
          tenants: { name: string; slug: string | null; is_internal: boolean | null } | null;
        }
      >,
    );
  },

  async listDeviceHeartbeats(): Promise<
    Result<
      Array<Pick<TenantDeviceRow, 'tenant_id' | 'app_version' | 'last_heartbeat_at'>>,
      AppError
    >
  > {
    const { data, error } = await supabase
      .from('tenant_devices')
      .select('tenant_id, app_version, last_heartbeat_at');
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listDeviceHeartbeats'));
    return ok(data ?? []);
  },

  /**
   * Met une commande en file pour UN terminal (super_admin, tracé au journal).
   * Les commandes qui ferment ou rechargent la caisse attendent qu'elle soit inactive.
   */
  async enqueueDeviceCommand(params: {
    type: DeviceCommandType;
    tenantId: string;
    deviceId: string;
    notes?: string | null;
  }): Promise<Result<string, AppError>> {
    const { data, error } = await supabase.rpc('enqueue_device_command', {
      p_command_type: params.type,
      p_tenant_id: params.tenantId,
      p_device_id: params.deviceId,
      p_payload: {},
      p_notes: params.notes ?? undefined,
    });
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.enqueueDeviceCommand'));
    return ok(String(data));
  },

  /** Désactive les overrides actifs d'un device puis en pose un nouveau. */
  async replaceDeviceOverride(
    deviceId: string,
    payload: Tables['tenant_device_overrides']['Insert'],
  ): Promise<Result<void, AppError>> {
    const { error: deactivateError } = await supabase
      .from('tenant_device_overrides')
      .update({ active: false })
      .eq('device_id', deviceId)
      .eq('active', true);
    if (deactivateError) {
      return err(appErrorFromSupabase(deactivateError, 'AdminOpsApi.replaceDeviceOverride'));
    }
    const { error } = await supabase.from('tenant_device_overrides').insert(payload);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.replaceDeviceOverride'));
    return ok(undefined);
  },

  // ── assistance_alert_rules / events ──────────────────────────────────
  async listAlertRules(): Promise<Result<AssistanceAlertRuleRow[], AppError>> {
    const { data, error } = await supabase
      .from('assistance_alert_rules')
      .select('*')
      .order('label');
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listAlertRules'));
    return ok(data ?? []);
  },

  async setAlertRuleEnabled(ruleId: string, enabled: boolean): Promise<Result<void, AppError>> {
    const { error } = await supabase
      .from('assistance_alert_rules')
      .update({ enabled })
      .eq('id', ruleId);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.setAlertRuleEnabled'));
    return ok(undefined);
  },

  async listAlertEvents(limit = 200): Promise<Result<AssistanceAlertEventRow[], AppError>> {
    const { data, error } = await supabase
      .from('assistance_alert_events')
      .select('*')
      .order('fired_at', { ascending: false })
      .limit(limit);
    if (error) return err(appErrorFromSupabase(error, 'AdminOpsApi.listAlertEvents'));
    return ok(data ?? []);
  },
};
